import { useEffect, useRef } from "react";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import type { ArraySchema, ObjectSchema } from "yup";

import type {
  FormBuilderField,
  MultipleCopiesFormBuilderField,
} from "@thalamos/aspire-common";

import type { FieldProps, RenderField } from "../../pages/FieldProps.js";
import { HelperText } from "../design-system/HelperText/HelperText.js";
import { Button, ButtonIcon } from "../design-system/index.js";

export type MultipleCopiesFormFieldProps<Data extends { [k: string]: any }> = {
  field: MultipleCopiesFormBuilderField<Data, string>;
  fieldProps: FieldProps<Data>;
  renderField: RenderField;
  disabled?: boolean;
};

export function MultipleCopiesFormField<Data extends { [k: string]: any }>({
  field,
  fieldProps,
  renderField,
}: MultipleCopiesFormFieldProps<Data>) {
  const theme = useTheme();
  const elementRef = useRef<HTMLDivElement>(null);
  const scrollOnAddRef = useRef(false);

  const valuesArray = (fieldProps.values[field.field] as object[]) ?? [];
  const rawErrors = fieldProps.errors[field.field];
  const rawTouched = fieldProps.touched[field.field];
  const errorsArray = Array.isArray(rawErrors) ? (rawErrors as object[]) : [];
  const touchedArray = Array.isArray(rawTouched)
    ? (rawTouched as object[])
    : [];

  // A non-array (string) error at the array key is the array-level error
  // (e.g. the empty drugEntries "Please add medication details"). Surface it
  // beneath the add button.
  const arrayLevelError = typeof rawErrors === "string" ? rawErrors : undefined;
  const showArrayError = !!(rawTouched && arrayLevelError);

  useEffect(() => {
    if (scrollOnAddRef.current) {
      elementRef.current?.scrollIntoView({ behavior: "smooth" });
      scrollOnAddRef.current = false;
    }
  }, [valuesArray.length]);

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {valuesArray.map((copyValues, index) => (
          <Collapse in={true} timeout={300} key={index}>
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                pl: 2,
                py: 2,
                px: 1,
                borderRadius: 2,
                boxShadow: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {field.titleFn?.(index) ?? index + 1}
              </Typography>
              <Box ref={elementRef} />
              {field.fields.map((subField) =>
                renderField(
                  subField as FormBuilderField<any>,
                  {
                    ...fieldProps,
                    validationSchema: (
                      fieldProps.validationSchema.fields[
                        field.field
                      ] as ArraySchema<any, object>
                    ).innerType,
                    values: copyValues,
                    setValues: (values: any) => {
                      const newArray = [
                        ...valuesArray.slice(0, index),
                        values,
                        ...valuesArray.slice(index + 1),
                      ];
                      fieldProps.setValues({
                        ...fieldProps.values,
                        [field.field]: newArray,
                      });
                    },
                    errors: errorsArray[index] ?? {},
                    touched: touchedArray[index] ?? {},
                  } as FieldProps<any>,
                ),
              )}

              <Box sx={{ alignSelf: "flex-start" }}>
                <Button
                  color="error"
                  startIcon={ButtonIcon.delete}
                  label={field.removeCopyLabel}
                  variant="outlined"
                  onClick={() => {
                    const newArray = [
                      ...valuesArray.slice(0, index),
                      ...valuesArray.slice(index + 1),
                    ];
                    fieldProps.setValues({
                      ...fieldProps.values,
                      [field.field]: newArray,
                    });
                  }}
                />
              </Box>
            </Box>
          </Collapse>
        ))}

        <Box sx={{ mb: 3 }}>
          <Button
            label={field.addCopyLabel}
            startIcon={ButtonIcon.add}
            variant="outlined"
            color={showArrayError ? "error" : undefined}
            onClick={() => {
              scrollOnAddRef.current = true;
              const defaultValue = ((
                fieldProps.validationSchema.fields[field.field] as ArraySchema<
                  any,
                  object
                >
              ).innerType as ObjectSchema<any, any>)!.getDefault();

              fieldProps.setValues({
                ...fieldProps.values,
                [field.field]: [
                  ...fieldProps.values[field.field],
                  defaultValue,
                ],
              });
            }}
          />
          {showArrayError && (
            <Box sx={{ mt: 1 }}>
              <HelperText errorMessage={arrayLevelError} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
